// yip-cart-chip.js — CANONICAL cart-count + cart-change hub (single source of truth).
//
// Why this file owns everything cart-count related:
//   Previously the count was written by 6+ places (this file, header.liquid,
//   theme.liquid Boost hook, delist/calculator sliders, customization.js) and a
//   SECOND window.fetch monkey-patch lived in cart-sidebar.js. Those raced: on a
//   fast machine they happened to resolve in the "right" order, but on a slower /
//   differently-timed machine (e.g. Windows Chrome) the order shifted and the chip
//   showed a stale count. This hub removes the races:
//     1. It is the ONLY thing that writes .cart-chip.
//     2. It detects EVERY cart mutation via a single guarded window.fetch hook
//        (replacing the second patch in cart-sidebar.js).
//     3. Reads of /cart.js are SEQUENCED, so an older read can never overwrite a
//        newer one — the count is deterministic regardless of resolution order.
//     4. It publishes one canonical cartUpdate so other consumers (sidebar) react
//        without patching fetch or writing the chip themselves.
if (window.__CART_CHIP_INIT__) {
  /* already initialised */
} else {
  window.__CART_CHIP_INIT__ = true;
  (function () {
    // Matches only cart MUTATIONS (never /cart.js reads or /cart page loads),
    // so the authoritative refresh below can't re-trigger itself.
    var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

    // --- state --------------------------------------------------------------
    var lastCount = 0;
    var rafId = null;
    var fetchTimer = null;
    var seq = 0;        // id assigned to each /cart.js read
    var appliedSeq = 0; // highest read id whose result has been applied

    // --- dom helpers --------------------------------------------------------
    var $chips = function () { return document.querySelectorAll('.cart-chip, [data-cart-count]'); };

    function setCountOnEl(el, count) {
      if (el.hasAttribute('data-cart-count')) {
        el.setAttribute('data-cart-count', String(count));
      } else {
        var target = el.querySelector('[data-cart-count-target]') || el;
        target.textContent = String(count);
      }
      el.classList.toggle('active', count > 0);
      el.setAttribute('aria-live', 'polite');
    }

    function paint(count) {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        $chips().forEach(function (el) { setCountOnEl(el, count); });
      });
    }

    // Optimistic / event-driven render (no network). Never regresses to a value
    // we already know is older.
    function render(count) {
      var n = Number(count) || 0;
      if (n === lastCount) { if ($chips().length) paint(n); return; }
      lastCount = n;
      paint(n);
    }

    // --- authoritative, race-proof read ------------------------------------
    function authoritativeFetch() {
      var mySeq = ++seq;
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('cart fetch failed'); return r.json(); })
        .then(function (cart) {
          if (mySeq < appliedSeq) return; // a newer read already won — ignore stale
          appliedSeq = mySeq;
          render(cart && typeof cart.item_count === 'number' ? cart.item_count : 0);
          return cart;
        })
        .catch(function () { /* keep last known count */ });
    }

    function scheduleRefresh(delay) {
      clearTimeout(fetchTimer);
      fetchTimer = setTimeout(authoritativeFetch, typeof delay === 'number' ? delay : 120);
    }

    // NOTE: the hub intentionally does NOT publish cartUpdate. The theme already
    // has ~14 cartUpdate publishers and ~13 subscribers; re-publishing here would
    // make every subscriber fire twice per mutation. The hub only CONSUMES events
    // (+ its own fetch-hook) to own the count; other consumers (e.g. cart-sidebar)
    // subscribe to the existing publishers.

    function deriveCount(ev) {
      if (ev && ev.cartData && typeof ev.cartData.item_count === 'number') return ev.cartData.item_count;
      if (ev && typeof ev.item_count === 'number') return ev.item_count;
      if (ev && Array.isArray(ev.items)) {
        return ev.items.reduce(function (s, it) { return s + (Number(it && it.quantity) || 0); }, 0);
      }
      return null;
    }

    // --- single, guarded universal cart-mutation detector -------------------
    // Replaces the second window.fetch patch (formerly in cart-sidebar.js) and
    // every direct .cart-chip write across the theme. Any /cart/add|change|
    // update|clear success schedules ONE authoritative, sequenced refresh.
    if (!window.__YIP_CART_FETCH_HOOK__) {
      window.__YIP_CART_FETCH_HOOK__ = true;
      var _fetch = window.fetch;
      window.fetch = function () {
        var res = _fetch.apply(this, arguments);
        try {
          var resource = arguments[0];
          var url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
          if (url && CART_MUTATION_RE.test(url) && res && typeof res.then === 'function') {
            res.then(function (r) { if (r && r.ok) scheduleRefresh(80); }).catch(function () {});
          }
        } catch (e) { /* never break fetch */ }
        return res;
      };
    }

    // --- repaint (not recount) when chips are (re)inserted ------------------
    // Only ever repaints the authoritative lastCount, so it can't fight a writer
    // (there are no other writers anymore).
    var mo = new MutationObserver(function () { if ($chips().length) paint(lastCount); });
    function startObserver() {
      if (document.body) mo.observe(document.body, { childList: true, subtree: true });
      else window.addEventListener('DOMContentLoaded', function () {
        mo.observe(document.body, { childList: true, subtree: true });
      });
    }
    startObserver();

    document.addEventListener('shopify:section:load', function () { paint(lastCount); });
    document.addEventListener('shopify:section:unload', function () { paint(lastCount); });
    window.addEventListener('pageshow', function () { scheduleRefresh(0); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') scheduleRefresh(0);
    });

    // --- pubsub wiring ------------------------------------------------------
    function ready(cb) {
      if (typeof PUB_SUB_EVENTS !== 'undefined' && window.Shopify && window.Shopify.pubsub) cb();
      else setTimeout(function () { ready(cb); }, 50);
    }
    ready(function () {
      // Authoritative cart snapshots from anywhere (Boost integration, sliders,
      // cart.js, product-form, gift-wrap…). If the payload carries a count we
      // render it instantly; otherwise we confirm against /cart.js. Unknown
      // payload shapes are therefore always safe.
      Shopify.pubsub.subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
        var c = deriveCount(ev);
        if (typeof c === 'number') render(c);
        else scheduleRefresh();
      });
      // Optimistic UI deltas (e.g. cart page qty steppers).
      Shopify.pubsub.subscribe(PUB_SUB_EVENTS.quantityUpdate, function (ev) {
        if (ev && typeof ev.delta === 'number' && ev.delta !== 0) {
          render(Math.max(0, lastCount + ev.delta));
          scheduleRefresh(); // confirm against server
          return;
        }
        var c = deriveCount(ev);
        if (typeof c === 'number') render(c);
      });
      scheduleRefresh(0); // initial paint
    });

    // --- public API for code paths that can't publish pubsub ---------------
    // (e.g. the Boost updateCartIconActionForThemes hook in theme.liquid).
    window.YipCart = window.YipCart || {};
    window.YipCart.refresh = function () { scheduleRefresh(0); };          // authoritative
    window.YipCart.set = function (count) { render(count); scheduleRefresh(); }; // optimistic + confirm
  })();
}
